// The CURRENT workspace — as opposed to `useAuth(s => s.organization)`,
// which is the user's HOME org and never updates when they switch into
// another workspace via /w/:wsId (see authStore.ts's field comment for the
// full story, and the AppShell/PeopleSection/DetailsPanel/etc. bugs that
// came from conflating the two).
//
// useActiveOrgId() lives in authStore.ts (it's a one-line derivation of
// authStore's own state, no extra data source needed). useActiveWorkspaceName()
// lives here because it also needs workspaceStore — combining the two here
// keeps authStore.ts and workspaceStore.ts free of a dependency on each
// other (workspaceStore doesn't know about authStore; authStore only reaches
// into workspaceStore to reset() it on logout).

import { useEffect } from 'react';
import { useAuth, useActiveOrgId } from '../stores/authStore';
import { useWorkspaceStore } from '../stores/workspaceStore';

export { useActiveOrgId };

/**
 * The display name of the workspace the user is currently working in.
 *
 * Resolution order:
 *   1. A match for the active org id (or the home workspace, when no
 *      non-home org is active) in the cached GET /api/v1/workspaces list —
 *      this is the only source that's actually correct after a workspace
 *      switch.
 *   2. The HOME org name from login (`organization.name`, falling back to
 *      the legacy `user.organization_name` stopgap some login flows set) —
 *      used while the workspace list is still loading or if the fetch
 *      failed, so callers never render a blank while waiting on network.
 *   3. `fallback` (default: '') if neither is available yet.
 *
 * Triggers the one-time, shared workspace fetch on first use — see
 * workspaceStore.ensureLoaded, which is idempotent, so every consumer of
 * this hook calling it costs nothing beyond the first.
 */
export function useActiveWorkspaceName(fallback = ''): string {
  const activeOrgId = useActiveOrgId();
  const homeOrgName = useAuth(s =>
    s.organization?.name || (s.user as { organization_name?: string } | null)?.organization_name);
  const workspaces   = useWorkspaceStore(s => s.workspaces);
  const status       = useWorkspaceStore(s => s.status);
  const ensureLoaded = useWorkspaceStore(s => s.ensureLoaded);

  useEffect(() => { ensureLoaded(); }, [ensureLoaded]);

  if (status === 'loaded') {
    const current = workspaces.find(w => w.id === activeOrgId)
      ?? (activeOrgId == null ? workspaces.find(w => w.is_home) : undefined);
    if (current) return current.name;
  }
  return homeOrgName || fallback;
}
